/* One product-gallery controller. No cart, pricing or purchase-state mutations. */
(() => {
  'use strict';
  const instances = new WeakMap();
  const init = gallery => {
    if (!gallery) return null;
    if (instances.has(gallery)) return instances.get(gallery);
    const main = gallery.querySelector('.gallery__main');
    const viewport = gallery.querySelector('.gallery__viewport');
    const rail = gallery.querySelector('[data-gallery-rail]');
    const thumbsRail = gallery.querySelector('[data-thumbs]');
    const all = [...gallery.querySelectorAll('[data-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
    if (!main || !all.length) return null;
    const mobile = matchMedia('(max-width:860px)');
    const reduced = matchMedia('(prefers-reduced-motion:reduce)');
    const limit = Number(gallery.dataset.galleryLimit) || 9;
    const words = document.documentElement.lang.startsWith('en')
      ? {open:'Enlarge image',close:'Close',prev:'Previous image',next:'Next image',fit:'Full image',zoom:'Zoom ×2',pan:'Drag to explore the details',tap:'Zoom in to see the details'}
      : {open:'Agrandir l’image',close:'Fermer',prev:'Image précédente',next:'Image suivante',fit:'Vue entière',zoom:'Zoom ×2',pan:'Glissez pour voir les détails',tap:'Agrandissez pour voir les détails'};
    const counter = document.createElement('span');
    counter.className = 'gallery__counter'; counter.setAttribute('aria-live','polite'); counter.setAttribute('aria-atomic','true');
    const zoomButton = document.createElement('button');
    zoomButton.type='button'; zoomButton.className='gallery__zoomhint'; zoomButton.setAttribute('aria-label',words.open);
    zoomButton.innerHTML='<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m16 16 5 5M11 8v6M8 11h6"/></svg>';
    viewport.append(counter,zoomButton);
    let visible=[], active=null, variant=null, scrollTimer, resizeTimer, layoutBusy=false, releaseFrame;
    let dialog, stage, zoomImage, toggle, zoomCount, hint, previous, next, opener, wasLocked;
    const images = () => visible.filter(s=>s.dataset.mediaType==='image' && s.querySelector('img'));
    const selectedThumb = () => thumbs.find(t=>t.dataset.thumb===active?.dataset.slide);
    const railNav = () => {
      const vertical=!mobile.matches;
      gallery.querySelector('[data-rail-up]')?.classList.toggle('is-visible',vertical && thumbsRail.scrollTop>4);
      gallery.querySelector('[data-rail-down]')?.classList.toggle('is-visible',vertical && thumbsRail.scrollTop+thumbsRail.clientHeight<thumbsRail.scrollHeight-4);
    };
    const revealThumb = () => {
      const thumb=selectedThumb(); if(!thumb || !thumbsRail) return;
      const t=thumb.getBoundingClientRect(), r=thumbsRail.getBoundingClientRect();
      if(mobile.matches && (t.left<r.left+2 || t.right>r.right-2)) thumbsRail.scrollTo({left:thumbsRail.scrollLeft+t.left-r.left-(r.width-t.width)/2,behavior:'instant'});
      if(!mobile.matches && (t.top<r.top+2 || t.bottom>r.bottom-2)) thumbsRail.scrollTo({top:thumbsRail.scrollTop+t.top-r.top-(r.height-t.height)/2,behavior:'instant'});
      railNav();
    };
    const activate = slide => {
      if(!slide || !visible.includes(slide)) return;
      all.forEach(s=>{
        const on=s===slide, wasActive=s.classList.contains('is-active');
        s.classList.toggle('is-active',on); s.toggleAttribute('inert',!on);
        if(!on && wasActive) {
          s.querySelectorAll('video').forEach(v=>v.pause());
          s.querySelectorAll('iframe').forEach(f=>{f.src=f.src;});
        }
      });
      active=slide;
      thumbs.forEach(t=>{const on=t.dataset.thumb===slide.dataset.slide;t.classList.toggle('is-active',on);t.setAttribute('aria-pressed',String(on));});
      counter.textContent=`${visible.indexOf(active)+1} / ${visible.length}`; counter.hidden=visible.length<2;
      zoomButton.hidden=active.dataset.mediaType!=='image';
      revealThumb();
    };
    const align = () => {
      if(thumbsRail) thumbsRail.style.maxHeight=mobile.matches?'':`${main.clientHeight}px`;
      if(active) main.scrollTo({left:mobile.matches?active.offsetLeft:0,behavior:'instant'});
      revealThumb();
    };
    const settleLayout = () => {
      cancelAnimationFrame(releaseFrame);
      releaseFrame=requestAnimationFrame(()=>{align();releaseFrame=requestAnimationFrame(()=>{layoutBusy=false;});});
    };
    const show = slide => {
      layoutBusy=true;clearTimeout(scrollTimer);activate(slide);align();settleLayout();
    };
    const selectVariant = (id,mediaId) => {
      id=String(id || gallery.dataset.initialVariant);
      if(variant===id) return;
      layoutBusy=true;clearTimeout(scrollTimer);variant=id;
      if(dialog?.open) dialog.close();
      visible=all.filter(s=>{const owners=(s.dataset.mediaVariants||'').trim().split(/\s+/).filter(Boolean);return !owners.length || owners.includes(id);}).slice(0,limit);
      all.forEach(s=>{s.hidden=!visible.includes(s);});
      thumbs.forEach(t=>{const index=visible.findIndex(s=>s.dataset.slide===t.dataset.thumb);t.hidden=index<0;if(index>=0&&t.dataset.mediaLabel)t.setAttribute('aria-label',t.dataset.mediaLabel.replace('__number__',String(index+1)));});
      rail.hidden=visible.length<2;
      // Preserve common editorial first views on load. On a new selection, show its own image.
      const preferred=visible.find(s=>mediaId && s.dataset.mediaId===String(mediaId));
      activate(preferred || visible[0]);
      align();settleLayout();
    };
    thumbs.forEach(t=>t.addEventListener('click',()=>show(all.find(s=>s.dataset.slide===t.dataset.thumb))));
    thumbsRail.addEventListener('keydown',e=>{
      if(!e.target.closest('[data-thumb]') || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) return;
      e.preventDefault();
      let i=visible.indexOf(active);
      if(e.key==='Home')i=0;else if(e.key==='End')i=visible.length-1;else i=(i+(/Right|Down/.test(e.key)?1:-1)+visible.length)%visible.length;
      show(visible[i]); selectedThumb()?.focus({preventScroll:true});
    });
    thumbsRail.addEventListener('scroll',railNav,{passive:true});
    for(const [selector,dir] of [['[data-rail-up]',-1],['[data-rail-down]',1]]) gallery.querySelector(selector)?.addEventListener('click',()=>thumbsRail.scrollBy({top:dir*thumbsRail.clientHeight*.8,behavior:reduced.matches?'instant':'smooth'}));
    main.addEventListener('scroll',()=>{
      if(layoutBusy || !mobile.matches)return;
      clearTimeout(scrollTimer); scrollTimer=setTimeout(()=>{
        if(layoutBusy)return;
        const nearest=visible.reduce((a,b)=>Math.abs(b.offsetLeft-main.scrollLeft)<Math.abs(a.offsetLeft-main.scrollLeft)?b:a,visible[0]);
        activate(nearest);
      },70);
    },{passive:true});
    const resize = () => {
      layoutBusy=true;clearTimeout(scrollTimer);clearTimeout(resizeTimer);
      resizeTimer=setTimeout(()=>{align();settleLayout();if(dialog?.open)setZoom(dialog.classList.contains('is-zoomed'));},80);
    };
    const observer = new ResizeObserver(resize);observer.observe(viewport);
    mobile.addEventListener('change',resize);

    const setZoom = (zoomed,center=true) => {
      dialog.classList.toggle('is-zoomed',zoomed);
      const ratio=(zoomImage.naturalWidth||1)/(zoomImage.naturalHeight||1);
      zoomImage.style.width=`${Math.min(stage.clientWidth,stage.clientHeight*ratio)*(zoomed?2:1)}px`;
      toggle.textContent=zoomed?words.fit:words.zoom;toggle.setAttribute('aria-pressed',String(zoomed));
      hint.textContent=zoomed?words.pan:words.tap;
      if(center)requestAnimationFrame(()=>stage.scrollTo({left:zoomed?(stage.scrollWidth-stage.clientWidth)/2:0,top:zoomed?(stage.scrollHeight-stage.clientHeight)/2:0,behavior:'instant'}));
    };
    const renderZoom = () => {
      const source=active.querySelector('img'), group=images();
      zoomImage.alt=source.alt;zoomImage.src=(source.currentSrc||source.src).replace(/([?&]width=)\d+/,(_,p)=>p+'1600');
      zoomCount.textContent=`${group.indexOf(active)+1} / ${group.length}`;
      previous.hidden=next.hidden=group.length<2;setZoom(false);
    };
    const advanceZoom = delta => {const group=images();show(group[(group.indexOf(active)+delta+group.length)%group.length]);renderZoom();};
    const buildZoom = () => {
      dialog=document.createElement('dialog');dialog.className='product-zoom';dialog.setAttribute('aria-label',words.open);
      dialog.innerHTML='<div class="product-zoom__head"><span class="product-zoom__hint"></span><button type="button" class="product-zoom__close">×</button></div><div class="product-zoom__stage"><img class="product-zoom__img" alt="" draggable="false"></div><div class="product-zoom__foot"><button type="button" data-zoom-prev>‹</button><span class="product-zoom__counter" aria-live="polite"></span><button type="button" data-zoom-next>›</button><button type="button" data-zoom-toggle></button></div>';
      document.body.append(dialog);
      stage=dialog.querySelector('.product-zoom__stage');zoomImage=dialog.querySelector('img');toggle=dialog.querySelector('[data-zoom-toggle]');zoomCount=dialog.querySelector('.product-zoom__counter');hint=dialog.querySelector('.product-zoom__hint');previous=dialog.querySelector('[data-zoom-prev]');next=dialog.querySelector('[data-zoom-next]');
      const close=dialog.querySelector('.product-zoom__close');close.setAttribute('aria-label',words.close);previous.setAttribute('aria-label',words.prev);next.setAttribute('aria-label',words.next);
      close.addEventListener('click',()=>dialog.close());
      dialog.addEventListener('close',()=>{if(!wasLocked)document.body.classList.remove('no-scroll');opener?.focus({preventScroll:true});});
      previous.addEventListener('click',()=>advanceZoom(-1));next.addEventListener('click',()=>advanceZoom(1));toggle.addEventListener('click',()=>setZoom(!dialog.classList.contains('is-zoomed')));
      zoomImage.addEventListener('load',()=>{if(dialog.open)setZoom(dialog.classList.contains('is-zoomed'));});
      let drag=null,moved=false;
      stage.addEventListener('pointerdown',e=>{moved=false;drag={x:e.clientX,y:e.clientY,left:stage.scrollLeft,top:stage.scrollTop,type:e.pointerType};if(e.pointerType==='mouse'&&dialog.classList.contains('is-zoomed'))stage.setPointerCapture(e.pointerId);});
      stage.addEventListener('pointermove',e=>{if(!drag)return;const dx=e.clientX-drag.x,dy=e.clientY-drag.y;if(Math.abs(dx)+Math.abs(dy)>8)moved=true;if(drag.type==='mouse'&&dialog.classList.contains('is-zoomed')){stage.scrollLeft=drag.left-dx;stage.scrollTop=drag.top-dy;}});
      stage.addEventListener('pointerup',()=>{drag=null;});stage.addEventListener('pointercancel',()=>{drag=null;});
      // touchend remains delivered after native pan starts (pointercancel), so fit-view swipe works.
      let touch=null;
      stage.addEventListener('touchstart',e=>{touch=e.touches.length===1?{x:e.touches[0].clientX,y:e.touches[0].clientY}:null;},{passive:true});
      stage.addEventListener('touchend',e=>{if(touch&&!dialog.classList.contains('is-zoomed')){const dx=e.changedTouches[0].clientX-touch.x,dy=e.changedTouches[0].clientY-touch.y;if(Math.abs(dx)>50&&Math.abs(dx)>Math.abs(dy)){moved=true;advanceZoom(dx<0?1:-1);}}touch=null;},{passive:true});
      zoomImage.addEventListener('click',()=>{if(!moved)setZoom(!dialog.classList.contains('is-zoomed'));});
      stage.addEventListener('click',e=>{if(e.target===stage&&!moved)dialog.close();});
      dialog.addEventListener('keydown',e=>{if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();advanceZoom(e.key==='ArrowRight'?1:-1);}});
    };
    const openZoom = () => {
      if(!active || active.dataset.mediaType!=='image')return;
      if(!dialog)buildZoom();opener=document.activeElement;wasLocked=document.body.classList.contains('no-scroll');
      dialog.showModal();document.body.classList.add('no-scroll');renderZoom();
    };
    zoomButton.addEventListener('click',openZoom);
    let start=null,swiped=false;
    main.addEventListener('pointerdown',e=>{start={x:e.clientX,y:e.clientY};swiped=false;},{passive:true});
    main.addEventListener('pointermove',e=>{if(start&&Math.abs(e.clientX-start.x)+Math.abs(e.clientY-start.y)>10)swiped=true;},{passive:true});
    main.addEventListener('pointercancel',()=>{swiped=true;start=null;},{passive:true});
    main.addEventListener('click',e=>{if(swiped||e.target.closest('button,a,video,iframe,model-viewer'))return;openZoom();});
    const api={selectVariant,destroy:()=>{observer.disconnect();mobile.removeEventListener('change',resize);clearTimeout(scrollTimer);clearTimeout(resizeTimer);cancelAnimationFrame(releaseFrame);if(dialog?.open)dialog.close();dialog?.remove();}};
    instances.set(gallery,api);selectVariant(gallery.dataset.initialVariant);gallery.dataset.galleryReady='true';
    return api;
  };
  window.GSProductGallery={init};
  const scan=scope=>scope.querySelectorAll('[data-gallery]').forEach(init);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>scan(document),{once:true});else scan(document);
  document.addEventListener('shopify:section:load',e=>scan(e.target));
  document.addEventListener('shopify:section:unload',e=>e.target.querySelectorAll('[data-gallery]').forEach(g=>instances.get(g)?.destroy()));
})();
